import { useQuery } from '@apollo/client';
import Background from '@do/walrus/Background';
import Button from '@do/walrus/Button';
import DOLink from '@do/walrus/DOLink';
import Link from '@do/walrus/Link';
import { LoadingMessageWrapper } from '@do/walrus/SpinnerButton';
import StepTransitionManager from '@do/walrus/StepTransitionManager';
import Text from '@do/walrus/Text';
import useOneTimeCookie from '@do/walrus/hooks/useOneTimeCookie';
import colors from '@do/walrus/scss/core/_colors.scss';
import vars from '@do/walrus/scss/core/_variables.scss';
import LoadingState from '@do/walrus/states/Loading';
import _get from 'lodash/get';
import React, { ReactElement, useCallback, useContext, useState } from 'react';
import { RouteComponentProps } from 'react-router-dom';
import styled from 'styled-components';
import logoCreditSrc from '../assets/logo-credit.svg';
import logoEmailSrc from '../assets/logo-email.svg';
import { createClient } from '../client';
import { CHURN_SURVEY_URL, REGISTER_ENDPOINT } from '../constants';
import copy from '../copy';
import { GetRegistrationInfo } from '../generated-types/GetRegistrationInfo';
import useDocumentTitle from '../hooks/use-document-title';
import useSegmentTracking from '../hooks/use-segment-tracking';
import useSift from '../hooks/use-sift';
import { VariantBackgroundImage } from '../interfaces/registration';
import ArkoseContext, { ArkoseState } from '../providers/ArkoseContext';
import { GET_REGISTRATION_FORM_DATA } from '../queries/queries';
import { getSessionData } from '../utils/gql-utils';
import { getQueryParam } from '../utils/query-utils';
import { getVariantFromQuery } from '../utils/string-utils';
import {
  BannerType,
  ButtonImage,
  ErrorBanner,
  FormWrap,
  GithubButton,
  GoogleButton,
  HeaderBanner,
  Loading,
  PaddedContents,
  Wrap
} from './Common';
import Header from './Header';
import Metadata from './Metadata';
import RegistrationForm from './RegistrationForm';

const CreditBanner = styled.div`
  background: ${colors.blue_lightest};
  padding: ${vars.whitespace_regular} ${vars.whitespace_medium};
  margin: -${vars.space_4} 0 ${vars.space_4};
  border-bottom: ${vars.border_width} solid ${colors.blue_darkest};
`;

const StepPaddedContents = styled(PaddedContents)`
  padding-bottom: ${vars.space_2};
`;

const CreditText = styled(Text)`
  margin: ${vars.whitespace_xsmall} 0 0;
  font-size: ${vars.font_size_small};
`;

const CreditImage = styled.img`
  margin: -${vars.whitespace_xsmall} ${vars.whitespace_small} 0;
  width: ${vars.space_4};
`;

const FooterWrap = styled(Text)`
  margin: ${vars.whitespace_regular} 0 0;
  font-size: ${vars.font_size_small};
`;

const LoadingStateWrapper = styled.div`
  margin-top: ${vars.whitespace_regular};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const RedirectingMessage = styled(Text)`
  margin: 0 0 0 ${vars.space_3};
`;

const client = createClient(REGISTER_ENDPOINT);
const userUUID = getSessionData('ajs_user_id').replace(/(^"|"$)/g, '');
const churnSurvey = userUUID
  ? `${CHURN_SURVEY_URL}?uuid=${userUUID}`
  : CHURN_SURVEY_URL;

function Registration({ location }: RouteComponentProps): ReactElement {
  const [step, setStep] = React.useState(0);
  const isUserAccountDeactivated = useOneTimeCookie(
    '_digitalocean_flash_code',
    'user_account_deactivated'
  );

  const onboardingOrigin =
    getQueryParam('onboarding_origin', location.search) || '';

  const { loading: isLoadingFormData, data } = useQuery<GetRegistrationInfo>(
    GET_REGISTRATION_FORM_DATA,
    {
      client,
      variables: {
        onboarding_origin: onboardingOrigin,
        redirect_url: getQueryParam('redirect_url', location.search)
      }
    }
  );
  const sessionId = _get(data, 'registrationFormData.session_id');

  const variant = !isLoadingFormData && getVariantFromQuery(data);
  useDocumentTitle(_get(variant, 'title'));
  useSift(_get(data, 'registrationFormData.session_id'));
  useSegmentTracking(
    'Registration Variant',
    {
      variantType: _get(variant, 'type')
    },
    isLoadingFormData
  );
  const { run, reset, state: arkoseState, error: arkoseError } = useContext(
    ArkoseContext
  );
  const [isLoadingOauthPath, setIsLoadingOauthPath] = useState(false);

  const hasArkoseFailedToLoad = arkoseState === ArkoseState.LOADING_FAILED;
  const oauthLinkClickHandler = useCallback(
    (url: string) => {
      return async (e: MouseEvent) => {
        e.preventDefault();
        setIsLoadingOauthPath(true);
        try {
          await run(sessionId);
          window.location.assign(url);
        } catch (_e) {
          reset();
        } finally {
          setIsLoadingOauthPath(false);
        }
      };
    },
    [run, reset, sessionId]
  );

  if (!variant) {
    return <Loading />;
  }

  const oauthError = _get(data, 'registrationFormData.oauth_error');

  const googleOauthUrl = `/registrations/google${location.search}`;
  const githubOauthUrl = `/registrations/github${location.search}`;
  const signInUrl = _get(
    data,
    'registrationFormData.marketplace_app.create_app_url'
  );
  const registrationEmail = _get(data, 'registrationFormData.email');

  const OauthSection = (): ReactElement => (
    <>
      <Text>
        {copy.form.oauth.title + ' '}
        <Link
          forceExternal={true}
          target="_self"
          to={googleOauthUrl}
          onClick={oauthLinkClickHandler(googleOauthUrl)}
          // disabled is not  valid property for an html a tag. We have styles that
          // makes it seem to be disabled, but an a tag cannot be disabled.
          {...({
            disabled: hasArkoseFailedToLoad || isLoadingOauthPath
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
          } as any)}
        >
          {copy.form.oauth.google}
        </Link>
        {' ' + copy.form.or + ' '}
        <Link
          forceExternal={true}
          target="_self"
          to={githubOauthUrl}
          onClick={oauthLinkClickHandler(githubOauthUrl)}
          // disabled is not  valid property for an html a tag. We have styles that
          // makes it seem to be disabled, but an a tag cannot be disabled.
          {...({
            disabled: hasArkoseFailedToLoad || isLoadingOauthPath
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
          } as any)}
        >
          {copy.form.oauth.github}
        </Link>
        {' ' + copy.form.oauth.instead}
      </Text>
    </>
  );

  const arkoseErrorBanner = arkoseError && (
    <ErrorBanner error={arkoseError || copy.errors.unknown} />
  );

  let form;
  if (step === 0) {
    const pretext = (
      <>
        {oauthError && (
          <ErrorBanner error={copy.errors[oauthError] || copy.errors.unknown} />
        )}
        <h4>{variant.title}</h4>
        {variant.metadata ? (
          <Metadata variant={variant} data={data} />
        ) : (
          <Text>{variant.subtitle}</Text>
        )}
      </>
    );

    if (arkoseState === ArkoseState.LOADING) {
      form = (
        <>
          {pretext}
          <LoadingStateWrapper>
            <LoadingState aria-label="loading-state" />
          </LoadingStateWrapper>
        </>
      );
    } else if (variant.type === 'team' && registrationEmail) {
      form = (
        <>
          {pretext}
          <OauthSection />
          {arkoseErrorBanner}
          <RegistrationForm
            sessionId={sessionId as string}
            email={registrationEmail}
            onboardingOrigin={onboardingOrigin}
            isLoadingOauthPath={isLoadingOauthPath}
          />
        </>
      );
    } else {
      form = (
        <>
          {pretext}
          {arkoseErrorBanner}
          <Button
            fullWidth={true}
            onClick={() => {
              reset();
              setStep(1);
            }}
            disabled={isLoadingOauthPath || hasArkoseFailedToLoad}
          >
            <ButtonImage src={logoEmailSrc} />
            {copy.form.email_signup_title}
          </Button>

          <GoogleButton
            url={googleOauthUrl}
            text={copy.form.oauth.register.google_button}
            onClick={oauthLinkClickHandler(googleOauthUrl)}
            disabled={isLoadingOauthPath || hasArkoseFailedToLoad}
          />

          <GithubButton
            url={githubOauthUrl}
            text={copy.form.oauth.register.github_button}
            onClick={oauthLinkClickHandler(githubOauthUrl)}
            disabled={isLoadingOauthPath || hasArkoseFailedToLoad}
          />
        </>
      );
    }
  } else {
    form = (
      <>
        <h4>{copy.form.email_signup_title}</h4>
        {arkoseState === ArkoseState.LOADING ? (
          <LoadingStateWrapper>
            <LoadingState aria-label="loading-state" />
          </LoadingStateWrapper>
        ) : (
          <>
            <OauthSection />
            {arkoseErrorBanner}
            <RegistrationForm
              sessionId={sessionId as string}
              email={registrationEmail}
              onboardingOrigin={onboardingOrigin}
              isLoadingOauthPath={isLoadingOauthPath}
            />
          </>
        )}
      </>
    );
  }
  let registrationBackground: React.ReactNode;
  if (variant && React.isValidElement(variant.background)) {
    registrationBackground = variant.background as React.ReactNode;
  } else if (variant) {
    const variantBackground = variant.background as VariantBackgroundImage;

    registrationBackground = (
      <Background
        horizontalPosition={variantBackground.horizontalPosition}
        verticalPosition={variantBackground.verticalPosition}
        url={variantBackground.image}
      />
    );
  }

  return (
    <>
      <Header
        forceExternal={!!signInUrl}
        url={signInUrl}
        queryString={location.search}
      />
      {isUserAccountDeactivated && (
        <HeaderBanner
          type={BannerType.Success}
          text={copy.deactivation_message}
          linkText={copy.survey_text}
          url={churnSurvey}
        />
      )}
      {registrationBackground}
      <Wrap>
        <FormWrap>
          {_get(data, 'registrationFormData.is_credit') && (
            <CreditBanner>
              <strong>
                {copy.credit.title}
                <CreditImage src={logoCreditSrc} />
              </strong>
              <CreditText>{copy.credit.details}</CreditText>
            </CreditBanner>
          )}
          <StepTransitionManager currentStepNum={step}>
            <StepPaddedContents>
              <>
                {form}
                {isLoadingOauthPath && (
                  <LoadingMessageWrapper loadingMessageAlignment="center">
                    <LoadingState aria-label="loading-state" />
                    <RedirectingMessage>Redirecting...</RedirectingMessage>
                  </LoadingMessageWrapper>
                )}
              </>
            </StepPaddedContents>
          </StepTransitionManager>

          <PaddedContents>
            <FooterWrap>
              {copy.footer.terms_before_link + ' '}
              <DOLink path="/legal/terms/">
                {copy.footer.terms_link_text}
              </DOLink>
              {' ' + copy.footer.privacy_before_link + ' '}
              <DOLink path="/legal/privacy/">
                {copy.footer.privacy_link_text}
              </DOLink>
            </FooterWrap>
          </PaddedContents>
        </FormWrap>
      </Wrap>
    </>
  );
}

export default Registration;
